import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import api from '../services/api';

const typeMap = {
  'recharge': '充值',
  'consumption': '消费',
  'consume': '消费',
  'purchase': '购买',
  'invite_reward': '邀请',
};

const TransactionRecord = ({ type: externalType }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  const type = externalType !== undefined ? externalType : 'all';

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await api.getUserTransactions();
      let data = response.data || response;
      if (data && data.data) data = data.data;
      if (!Array.isArray(data)) data = [];
      setTransactions(data.filter(t => t && (t._id || t.transactionId)));
    } catch (err) {
      console.error('加载交易记录失败:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [type]);

  useEffect(() => {
    setPage(0);
  }, [externalType]);

  const filteredTransactions = transactions.filter(t => {
    if (type === 'all') return true;
    if (type === 'consumption' && (t.type === 'consumption' || t.type === 'consume')) return true;
    return t.type === type;
  });

  const currentPage = filteredTransactions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);

  const getTypeLabel = (t) => {
    if (t.metadata?.featureLabel) return t.metadata.featureLabel;
    if (t.metadata?.feature) {
      const f = t.metadata.feature;
      if (f.includes('chapter') && f.includes('outline')) return '大纲';
      if (f.includes('chapter')) return '章节';
      if (f.includes('batch')) return '批量';
      if (f.includes('story')) return '故事';
    }
    return typeMap[t.type] || '消费';
  };

  const getAmountColor = (amount) => {
    return amount >= 0 ? '#00ff9f' : '#ff6b6b';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} sx={{ color: '#00ff9f' }} />
      </Box>
    );
  }

  if (currentPage.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3, color: 'rgba(255,255,255,0.4)' }}>
        <Typography sx={{ fontSize: '0.8rem' }}>暂无记录</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 记录列表 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {currentPage.map((t) => (
          <Box key={t._id || t.transactionId} sx={{ 
            bgcolor: 'rgba(0,0,0,0.25)', 
            borderRadius: 1.5, 
            p: { xs: 1.25, sm: 1.5 },
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ 
                  bgcolor: t.type === 'recharge' ? 'rgba(46,125,50,0.3)' : t.type === 'invite_reward' ? 'rgba(38,198,218,0.3)' : 'rgba(211,47,47,0.3)',
                  color: t.type === 'recharge' ? '#4caf50' : t.type === 'invite_reward' ? '#26c6da' : '#ef5350',
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.25,
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>
                  {getTypeLabel(t)}
                </Box>
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                {formatDate(t.createdAt)}
                {t.description && ` · ${t.description.slice(0, 20)}`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ 
                color: getAmountColor(t.amount), 
                fontWeight: 'bold', 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontFamily: 'monospace'
              }}>
                {t.amount >= 0 ? '+' : ''}{t.amount?.toLocaleString()}
              </Typography>
              {t.balance !== undefined && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
                  余额: {t.balance?.toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* 简洁分页 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
          <Button
            onClick={() => setPage(p => p - 1)}
            disabled={page === 0}
            size="small"
            sx={{
              minWidth: 28, height: 28, p: 0,
              color: '#00ff9f', bgcolor: 'transparent',
              fontSize: '0.8rem',
              '&:hover': { bgcolor: 'rgba(0,255,159,0.1)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' }
            }}
          >
            ‹
          </Button>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', mx: 0.5 }}>
            {page + 1}/{totalPages}
          </Typography>
          <Button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            size="small"
            sx={{
              minWidth: 28, height: 28, p: 0,
              color: '#00ff9f', bgcolor: 'transparent',
              fontSize: '0.8rem',
              '&:hover': { bgcolor: 'rgba(0,255,159,0.1)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' }
            }}
          >
            ›
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TransactionRecord;
